// components/ImageUpload.js
import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Upload, X, Trash2, ZoomIn, AlertCircle } from 'lucide-react';
import ENV from '../config/env';

const FILE_SERVER_URL = ENV.FILE_SERVER_URL;

const ImageUpload = ({ currentImage, onImageSelected, onImageRemoved }) => {
    const [preview, setPreview] = useState(currentImage);
    const [selectedFile, setSelectedFile] = useState(null);
    const [showLightbox, setShowLightbox] = useState(false);
    const [error, setError] = useState('');
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const fileInputRef = useRef(null);

    // Update preview when currentImage changes (for editing)
    useEffect(() => {
        setPreview(currentImage);
    }, [currentImage]);

    const getFullImageUrl = (imageUrl) => {
        if (!imageUrl) return null;
        if (imageUrl.startsWith('http')) return imageUrl;
        if (imageUrl.startsWith('blob:')) return imageUrl; // Local preview
        return `${FILE_SERVER_URL}${imageUrl}`;
    };

    const handleFileSelect = (e) => {
        e.stopPropagation();
        e.preventDefault();

        const file = e.target.files[0];
        if (!file) return;

        // Validate file type
        if (!file.type.startsWith('image/')) {
            setError('Por favor selecciona una imagen válida');
            return;
        }

        // Validate file size (5MB max)
        if (file.size > 5 * 1024 * 1024) {
            setError('La imagen no debe superar los 5MB');
            return;
        }

        // Clear error
        setError('');

        // Create local preview
        const localPreview = URL.createObjectURL(file);
        setPreview(localPreview);
        setSelectedFile(file);

        // Notify parent that a file has been selected (but not uploaded yet)
        onImageSelected(file);

        // Clear the file input
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    const handleRemoveClick = (e) => {
        e.stopPropagation();
        e.preventDefault();

        if (preview && preview.startsWith('blob:')) {
            // It's a local preview, just remove it
            URL.revokeObjectURL(preview);
            setPreview(null);
            setSelectedFile(null);
            onImageRemoved(false);
        } else if (preview) {
            // It's an existing image from server, show confirmation
            setShowDeleteConfirm(true);
        }
    };

    const handleConfirmDelete = async (e) => {
        e.stopPropagation();
        e.preventDefault();

        // For existing images, we need to notify parent to delete from server
        if (preview && !preview.startsWith('blob:')) {
            onImageRemoved(true); // true indicates it's a server image
        }

        // Clean up any blob URL
        if (preview && preview.startsWith('blob:')) {
            URL.revokeObjectURL(preview);
        }

        setPreview(null);
        setSelectedFile(null);
        setShowDeleteConfirm(false);
    };

    const handleCancelDelete = (e) => {
        e.stopPropagation();
        e.preventDefault();
        setShowDeleteConfirm(false);
    };

    const handleLightboxOpen = (e) => {
        e.stopPropagation();
        e.preventDefault();
        if (preview) {
            setShowLightbox(true);
        }
    };

    const handleLightboxClose = (e) => {
        e.stopPropagation();
        setShowLightbox(false);
    };

    const handleUploadClick = (e) => {
        e.stopPropagation();
        e.preventDefault();
        fileInputRef.current?.click();
    };

    const handleContainerClick = (e) => {
        e.stopPropagation();
        e.preventDefault();
    };

    return (
        <>
            <div className="space-y-4" onClick={handleContainerClick}>
                <label className="block text-sm font-medium mb-2">
                    Imagen del Anuncio
                </label>

                {error && (
                    <div className="bg-red-500/20 border border-red-500 rounded-lg p-3 flex items-center gap-2">
                        <AlertCircle className="text-red-500 flex-shrink-0" size={18} />
                        <p className="text-red-500 text-sm">{error}</p>
                    </div>
                )}

                {preview ? (
                    <motion.div
                        initial={{ opacity: 0, scale: 0.9 }}
                        animate={{ opacity: 1, scale: 1 }}
                        className="relative group"
                        onClick={handleContainerClick}
                    >
                        <div className="relative rounded-lg overflow-hidden border-2 border-gray-600">
                            <img
                                src={getFullImageUrl(preview)}
                                alt="Preview"
                                className="w-full h-48 object-cover cursor-pointer"
                                onClick={handleLightboxOpen}
                                onError={(e) => {
                                    e.target.onerror = null;
                                    e.target.src = `${FILE_SERVER_URL}/images/gallery/default.jpg`;
                                }}
                            />

                            {/* Overlay with buttons */}
                            <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                                <button
                                    onClick={handleLightboxOpen}
                                    className="p-2 bg-white/20 rounded-full hover:bg-white/30 text-white transition-colors"
                                    title="Ver imagen"
                                    type="button"
                                >
                                    <ZoomIn size={20} />
                                </button>
                                <button
                                    onClick={handleRemoveClick}
                                    className="p-2 bg-red-500/20 rounded-full hover:bg-red-500/30 text-red-400 transition-colors"
                                    title="Eliminar imagen"
                                    type="button"
                                >
                                    <Trash2 size={20} />
                                </button>
                            </div>
                        </div>

                        {preview.startsWith('blob:') && (
                            <p className="text-xs text-yellow-400 mt-2">
                                ⚠️ Imagen seleccionada (se subirá al guardar)
                            </p>
                        )}
                    </motion.div>
                ) : (
                    <div
                        onClick={handleUploadClick}
                        className="border-2 border-dashed border-gray-600 rounded-lg p-8 text-center cursor-pointer hover:border-primary transition-colors relative"
                    >
                        <>
                            <Upload className="mx-auto text-gray-400 mb-3" size={32} />
                            <p className="text-gray-400 mb-1">
                                Haz clic para seleccionar una imagen
                            </p>
                            <p className="text-sm text-gray-500">
                                PNG, JPG, GIF hasta 5MB
                            </p>
                        </>
                    </div>
                )}

                <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    onChange={handleFileSelect}
                    className="hidden"
                    onClick={(e) => e.stopPropagation()}
                />
            </div>

            {/* Delete Confirmation Modal */}
            <AnimatePresence>
                {showDeleteConfirm && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="fixed inset-0 z-[1100] flex items-center justify-center p-4"
                        onClick={handleCancelDelete}
                        style={{ backgroundColor: 'rgba(0, 0, 0, 0.5)' }}
                    >
                        <motion.div
                            initial={{ scale: 0.9 }}
                            animate={{ scale: 1 }}
                            exit={{ scale: 0.9 }}
                            className="bg-gray-800 rounded-xl p-6 max-w-sm w-full"
                            onClick={(e) => e.stopPropagation()}
                        >
                            <h3 className="text-xl font-bold text-white mb-4">
                                Confirmar eliminación
                            </h3>
                            <p className="text-gray-300 mb-6">
                                ¿Estás seguro de que quieres eliminar esta imagen? Esta acción no se puede deshacer.
                            </p>
                            <div className="flex justify-end gap-3">
                                <button
                                    onClick={handleCancelDelete}
                                    className="px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors"
                                    type="button"
                                >
                                    Cancelar
                                </button>
                                <button
                                    onClick={handleConfirmDelete}
                                    className="px-4 py-2 bg-red-500 hover:bg-red-600 text-white rounded-lg transition-colors flex items-center gap-2"
                                    type="button"
                                >
                                    <Trash2 size={18} />
                                    <span>Eliminar</span>
                                </button>
                            </div>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>

            {/* Lightbox Modal */}
            <AnimatePresence>
                {showLightbox && preview && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="fixed inset-0 z-[1200] flex items-center justify-center p-4"
                        style={{ backgroundColor: 'rgba(0, 0, 0, 0.95)' }}
                        onClick={handleLightboxClose}
                    >
                        <button
                            onClick={handleLightboxClose}
                            className="absolute top-4 right-4 text-white hover:text-gray-300 p-2 rounded-full bg-black/50 transition-colors"
                            type="button"
                        >
                            <X size={32} />
                        </button>

                        <motion.img
                            initial={{ scale: 0.9 }}
                            animate={{ scale: 1 }}
                            src={getFullImageUrl(preview)}
                            alt="Enlarged view"
                            className="max-w-full max-h-[90vh] object-contain rounded-lg"
                            onClick={(e) => e.stopPropagation()}
                            onError={(e) => {
                                e.target.onerror = null;
                                e.target.src = `${FILE_SERVER_URL}/images/gallery/default.jpg`;
                            }}
                        />
                    </motion.div>
                )}
            </AnimatePresence>
        </>
    );
};

export default ImageUpload;